import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The app's tab strip: an underline, and arrow keys that work.
 *
 * There were two strips and they had made opposite decisions. The task drawer
 * drew filled pills; the file preview drew underlines, and its own comment
 * rejected the pill treatment outright — "as small rounded pills they read as
 * text, and nothing said 'these switch what is below'". One surface had already
 * concluded the other's choice was wrong, which is as close to a settled
 * argument as a codebase gets, so the underline is what this primitive draws.
 *
 * Neither implemented roving tabindex, so both declared `role="tablist"` — a
 * promise that Left and Right move between tabs — and then left every tab in
 * the tab order with the arrows doing nothing. That is worse than plain
 * buttons: a keyboard reader is told a contract that is not kept.
 *
 * Deliberately not built on Base UI's `Tabs`, and this is the one place in
 * `ui/` that departs from that rule. Its `Root` has to contain the panels as
 * well as the list, and in both of these surfaces the panel is several layout
 * boxes away from the strip — inside a sheet's scrolling body in one, a sibling
 * pane in the other. Wrapping both would mean rebuilding two flex columns to
 * suit a component, rather than the other way round. What is actually shared
 * here is the treatment and the keyboard contract, and those are all this holds.
 */

export interface TabsListProps extends React.ComponentProps<'div'> {
  /** Names the strip for assistive technology. Required — a tablist without one is unnamed. */
  'aria-label': string;
}

/**
 * The strip. Arrow keys move between the tabs inside it, Home and End jump to
 * the ends, and the move activates — panels here are cheap to render, which is
 * the condition the automatic-activation pattern asks for.
 */
export function TabsList({
  className,
  onKeyDown,
  children,
  ...props
}: TabsListProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const moreToTheRight = useMoreToTheRight(ref, children);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const tabs = Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [],
    );
    if (tabs.length === 0) return;

    const current = tabs.findIndex((tab) => tab === document.activeElement);
    // A strip nobody has focused yet starts from the selected tab, so the first
    // arrow press moves one step rather than jumping to the beginning.
    const from =
      current === -1
        ? tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
        : current;

    let next: number;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else {
      const step = event.key === 'ArrowRight' ? 1 : -1;
      // Wraps, which is what the pattern specifies and what makes a three-tab
      // strip reachable in one press from either end.
      next = (from + step + tabs.length) % tabs.length;
    }

    event.preventDefault();
    tabs[next]?.focus();
    tabs[next]?.click();
  }

  return (
    <div
      ref={ref}
      role="tablist"
      onKeyDown={handleKeyDown}
      // The one fade every strip wears (spec 227 FR-011): the `overflow-fade`
      // utility reads this attribute, so the Library's tablist and the grouped
      // entries' link strips go on in the same way.
      data-overflowing={moreToTheRight ? 'true' : 'false'}
      // `overflow-y-hidden` beside `overflow-x-auto`, not instead of it: setting one
      // axis makes the strip a scroll container on both, and the selected tab's
      // underline overshoots the strip by a pixel — enough for classic-scrollbar
      // browsers to draw a vertical scrollbar that is nothing but two arrows.
      //
      // `shrink-0` because that overflow also removes the strip's automatic
      // minimum height. As a flex-column item it then shrinks to nothing the
      // moment the panel below is taller than the column — the Library lost its
      // tabs the day the catalog grew past one screen. A strip that switches the
      // panel must never be the thing that gives way to it.
      //
      // The fade says the strip goes on (spec 173): on a phone a fifth tab fell
      // past the edge, cut mid-word, with nothing to say it could be scrolled to.
      className={cn(
        'overflow-fade flex shrink-0 items-end gap-4 overflow-x-auto overflow-y-hidden',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Whether the strip runs on past its right edge, and keeps the selected tab in
 * view: a tab chosen from the keyboard, or one that was selected before the
 * strip got narrow, is never left behind the edge.
 */
function useMoreToTheRight(
  ref: React.RefObject<HTMLDivElement | null>,
  children: React.ReactNode,
) {
  const [more, setMore] = React.useState(false);
  const selected = React.useRef<Element | null>(null);

  // On the tabs, not on the strip's size: a count beside a tab name widens the
  // strip's contents without resizing it, which no observer would report.
  React.useEffect(() => {
    const strip = ref.current;
    if (!strip) return;
    const tab = strip.querySelector('[role="tab"][aria-selected="true"]');
    if (tab instanceof HTMLElement && tab !== selected.current) {
      selected.current = tab;
      const right = tab.offsetLeft + tab.offsetWidth - strip.offsetLeft;
      if (right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollLeft = right - strip.clientWidth;
      }
    }
    setMore(strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1);
  }, [ref, children]);

  React.useEffect(() => {
    const strip = ref.current;
    if (!strip) return;
    const update = () => {
      setMore(strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1);
    };
    strip.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      strip.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref]);

  return more;
}

export interface TabsTriggerProps extends Omit<
  React.ComponentProps<'button'>,
  'role' | 'aria-selected'
> {
  selected: boolean;
  /** The panel this tab controls, so the two are tied together. */
  panelId: string;
  /** An occasional number beside the name. A zero is not shown — see below. */
  count?: number | null;
}

/**
 * One tab.
 *
 * `tabIndex` is what makes the strip a single stop in the page's tab order:
 * only the selected tab takes Tab, and the arrows move within. Leaving every
 * tab focusable is the defect that made the arrow keys unnecessary and the
 * strip tedious.
 *
 * A zero count renders nothing at all. An empty tab says "nothing here yet" in
 * words once it is opened; a `0` beside its name says the same thing less
 * clearly, on every task, forever.
 */
export function TabsTrigger({
  selected,
  panelId,
  count,
  className,
  children,
  ...props
}: TabsTriggerProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      aria-controls={panelId}
      tabIndex={selected ? 0 : -1}
      className={cn(
        '-mb-px shrink-0 border-b-2 px-0.5 pt-1 pb-1.5 text-sm whitespace-nowrap transition-colors pointer-coarse:min-h-11 pointer-coarse:min-w-11',
        'focus-visible:ring-ring focus-visible:rounded-sm focus-visible:ring-3 focus-visible:outline-none',
        // Light Blue under the selected tab (spec 122 FR-012): the same line
        // the sidebar's active entry and the selected card carry.
        selected
          ? 'border-accent-line text-foreground font-medium'
          : 'text-muted-foreground hover:text-foreground hover:border-border border-transparent',
        className,
      )}
      {...props}
    >
      {children}
      {count !== null && count !== undefined && count > 0 ? (
        <span className="text-muted-foreground ml-1.5 text-xs tabular-nums">
          {count}
        </span>
      ) : null}
    </button>
  );
}
